import moment from 'moment';
import { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import useIndexDBValue from 'src/app/hook/useIndexDBValue';
import { seachUserMetadata } from 'src/app/modules/auth/core/_requests';
import { SelectEditor, useSelectEditorManager } from 'src/app/modules/component/select-editor';
import { TimeEditor } from 'src/app/modules/component/time-editor/TimeEditor';
import { useTimeEditorManager } from 'src/app/modules/component/time-editor/useTimeEditorManager';
import { updateOrderGroupFields } from 'src/app/modules/phan-he-kham-benh/services/ChiDinhDVService';
import { IDanhSachDanhMuc } from 'src/app/modules/phan-he-tiep-nhan-thanh-toan/models/TiepDonModel';
import { hasAuthority, KEY_AUTHORITIES } from 'src/app/modules/utils/Permission';
import ButtonIn from '../../../../component/button-in-phieu';
import { ConfirmDialog } from '../../../../component/ConfirmDialog';
import DanhSachPhieu from '../../../../component/DanhSachPhieu';
import InputSearch from '../../../../component/InputSearch';
import LabelRequired from '../../../../component/LabelRequired';
import { TableCustom } from '../../../../component/table/table-custom/TableCustom';
import { vatTuColumn } from '../../../../phan-he-kham-benh/constants/Columns';
import { WORKFLOW_MEDICAL_ITEM_STATUS_CODE } from '../../../../phan-he-kham-benh/constants/Thuoc';
import { IInPhieuChiDinhThuocParams } from '../../../../phan-he-kham-benh/models/ThuocModels';
import { deleteDonThuoc, huyGuiDonThuoc, printPhieuChiDinhThuoc } from '../../../../phan-he-kham-benh/services/ThuocService';
import ChiDinhVatTu from '../../../../phan-he-noi-tru-dieu-tri/components/chi-dinh-vat-tu';
import { getThongTinPhieuThuoc, updateKeThuoc } from '../../../../phan-he-noi-tru-dieu-tri/services/DieuTriServices';
import { APP_DATE_TIME_FORMAT, CODE, KEY, KEY_DS_DANH_MUC_TIEP_DON, ORDER_TYPE_ID, REQUEST_FROM, REQUEST_TYPE, RESPONSE_MESSAGE } from '../../../../utils/Constant';
import { formatDateTime } from '../../../../utils/FormatUtils';
import WfButton from '../../../../workflows/components/WfButton';
import { WfConstTrackerClinical } from '../../../../workflows/Constant';
import { usePhanHeNoiTruContext } from '../../../PhanHeNoiTruContext';
import { getDsPhieuThuocVatTu } from '../../../services/HanhChinhServices';

type Props = {
  treatmentSheetId?: string
  allowOrder?: boolean
}

const VatTu = ({ treatmentSheetId, allowOrder = true }: Props) => {

  const { benhNhanHanhChinh, statusChangedEvent } = usePhanHeNoiTruContext();
  const [dsPhieu, setDsPhieu] = useState<any[]>([]);
  const [selectedPhieu, setSelectedPhieu] = useState<any>(null);
  const [donVatTuInfo, setDonVatTuInfo] = useState<any>(null);

  const allowEdit = !benhNhanHanhChinh?.concluded && hasAuthority(KEY_AUTHORITIES.UPDATE_FIELDS.PHIEU_CHI_DINH);

  const { value: danhSachDanhMuc } = useIndexDBValue<IDanhSachDanhMuc>({
    key: KEY_DS_DANH_MUC_TIEP_DON,
  });

  const onUpdate = (updatedData: any, field: string) => {
    if (!updatedData?.[field]) return;
    // Cập nhật selectedPhieu với data mới
    setDonVatTuInfo((prev: any) => ({
      ...prev,
      [field]: updatedData[field]
    }));

    // Cập nhật trong danh sách phiếu
    setDsPhieu((prevList: any[]) =>
      prevList.map((phieu: any) =>
        phieu.id === donVatTuInfo?.id
          ? { ...phieu, [field]: updatedData[field] }
          : phieu
      )
    );
  }

  const doctorOrderDayManager = useTimeEditorManager({
    name: "doctorOrderDay",
    idFieldName: "orderGroupId",
    values: donVatTuInfo,
    updateFields: updateOrderGroupFields,
    onUpdateSuccess: (updatedData) => onUpdate(updatedData, "doctorOrderDay"),
  });

  const designatedDateManager = useTimeEditorManager({
    name: "designatedDate",
    idFieldName: "orderGroupId",
    values: donVatTuInfo,
    updateFields: updateOrderGroupFields,
    onUpdateSuccess: (updatedData) => onUpdate(updatedData, "designatedDate"),
  });

  const indicationPersonSelectManager = useSelectEditorManager({
    name: "designatedPerson",
    idFieldName: "orderGroupId",
    values: donVatTuInfo,
    updateFields: updateOrderGroupFields,
    buildPayload: (option) => ({
      designatedPerson: option?.fullName,
      indicationTime: donVatTuInfo?.designatedDate
    }),
    onUpdateSuccess: (updatedData) => onUpdate(updatedData, "designatedPerson"),
    enableConfirmWithReason: false, // Không dùng confirm with reason cho trường hình thức
  });

  const designatedPlaceSelectManager = useSelectEditorManager({
    name: "designatedPlace",
    idFieldName: "orderGroupId",
    values: donVatTuInfo,
    updateFields: updateOrderGroupFields,
    buildPayload: (option) => ({
      requestDeptCode: option?.code,
      requestDeptName: option?.name,
      designatedPlace: option?.name,
      indicationTime: donVatTuInfo?.designatedDate
    }),
    onUpdateSuccess: (updatedData) => onUpdate(updatedData, "designatedPlace"),
    enableConfirmWithReason: false, // Không dùng confirm with reason cho trường hình thức
  });

  const handleGetDsPhieu = async () => {
    try {
      let { data } = await getDsPhieuThuocVatTu(benhNhanHanhChinh?.treatmentId, ORDER_TYPE_ID.VAT_TU, treatmentSheetId);
      if (CODE.SUCCESS === data?.code) {
        let dsPhieuConverted = data?.data?.map((item: any) => {
          return {
            ...item,
            date: moment(item?.doctorOrderDay).format("DD/MM/YYYY"),
          };
        });
        setDsPhieu(dsPhieuConverted || []);
        setSelectedPhieu(null);
      } else {
        setDsPhieu([]);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handleSelectPhieu = async (id: any) => {
    try {
      let { data } = await getThongTinPhieuThuoc({ medicalItemGroupId: id });
      if (CODE.SUCCESS === data?.code) {
        setDonVatTuInfo(data?.data);
      } else {
        setDsPhieu([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (selectedPhieu?.id) {
      handleSelectPhieu(selectedPhieu?.id);
    } else {
      setDonVatTuInfo(null);
    }
  }, [selectedPhieu?.id]);

  useEffect(() => {
    benhNhanHanhChinh?.treatmentId && handleGetDsPhieu();
  }, [benhNhanHanhChinh?.treatmentId, statusChangedEvent, treatmentSheetId]);

  return (
    <Row className="h-100 spaces px-16 py-5">
      <div>
         <div className="spaces h-133 px-10 py-4 bg-white border-bottom border-bottom-2 d-flex">
          <Row className='mx-0'>
            <Col xs={4} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Loại phiếu:" className="spaces min-w-110" />
              <span className="fw-light text-truncate">
                {donVatTuInfo?.type || (donVatTuInfo?.id && 'Phiếu chỉ định') || '...'}
              </span>
            </Col>
            <Col xs={4} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Ngày y lệnh:" className="spaces min-w-110" />
              <TimeEditor
                name="doctorOrderDay"
                values={donVatTuInfo}
                isEditingTime={doctorOrderDayManager.isEditingTime}
                editedTime={doctorOrderDayManager.editedTime}
                showValidationConfirm={doctorOrderDayManager.showValidationConfirm}
                validationMessage={doctorOrderDayManager.validationMessage}
                onEdit={doctorOrderDayManager.handleEditTime}
                onCancel={doctorOrderDayManager.handleCancelEdit}
                onSave={doctorOrderDayManager.handleSaveTime}
                onConfirmValidation={doctorOrderDayManager.handleConfirmValidation}
                onCancelValidation={doctorOrderDayManager.handleCancelValidation}
                onTimeChange={doctorOrderDayManager.setEditedTime}
                allowEdit={allowEdit}
              />
            </Col>
            <Col xs={4} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Ngày chỉ định:" className="spaces min-w-110" />
              <TimeEditor
                name="designatedDate"
                values={donVatTuInfo}
                isEditingTime={designatedDateManager.isEditingTime}
                editedTime={designatedDateManager.editedTime}
                showValidationConfirm={designatedDateManager.showValidationConfirm}
                validationMessage={designatedDateManager.validationMessage}
                onEdit={designatedDateManager.handleEditTime}
                onCancel={designatedDateManager.handleCancelEdit}
                onSave={designatedDateManager.handleSaveTime}
                onConfirmValidation={designatedDateManager.handleConfirmValidation}
                onCancelValidation={designatedDateManager.handleCancelValidation}
                onTimeChange={designatedDateManager.setEditedTime}
                allowEdit={allowEdit}
              />
            </Col>
            <Col xs={4} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Mã phiếu:" className="spaces min-w-110" />
              <span className="fw-light text-truncate">{donVatTuInfo?.code || '...'}</span>
            </Col>
            <Col xs={8} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Nơi chỉ định:" className="spaces min-w-110" />
              <SelectEditor
                name="designatedPlace"
                values={{
                  designatedPlace: donVatTuInfo?.designatedPlace
                }}
                options={danhSachDanhMuc?.listPhongKham || []}
                inline={true}
                menuPlacement="top"
                isEditingSelect={designatedPlaceSelectManager.isEditingSelect}
                editedValue={designatedPlaceSelectManager.editedValue}
                showValidationConfirm={designatedPlaceSelectManager.showValidationConfirm}
                showConfirmWithReason={designatedPlaceSelectManager.showConfirmWithReason}
                validationMessage={designatedPlaceSelectManager.validationMessage}
                onEdit={designatedPlaceSelectManager.handleEditSelect}
                onCancel={designatedPlaceSelectManager.handleCancelEdit}
                onSave={designatedPlaceSelectManager.handleSaveSelect}
                onConfirmValidation={designatedPlaceSelectManager.handleConfirmValidation}
                onCancelValidation={designatedPlaceSelectManager.handleCancelValidation}
                onConfirmWithReason={designatedPlaceSelectManager.handleConfirmWithReason}
                onCancelWithReason={designatedPlaceSelectManager.handleCancelWithReason}
                onValueChange={designatedPlaceSelectManager.setEditedValue}
                allowEdit={allowEdit}
              />
            </Col>
            <Col xs={4} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Người chỉ định:" className="spaces min-w-110" />
              <SelectEditor
                name="designatedPerson"
                values={{
                  designatedPerson: donVatTuInfo?.designatedPerson
                }}
                options={[]}
                searchFunction={seachUserMetadata}
                searchObject={{ position: 1 }}
                urlData="data.content"
                getOptionLabel={(opt) => opt?.fullName || ""}
                inline={true}
                menuPlacement="top"
                isEditingSelect={indicationPersonSelectManager.isEditingSelect}
                editedValue={indicationPersonSelectManager.editedValue}
                showValidationConfirm={indicationPersonSelectManager.showValidationConfirm}
                showConfirmWithReason={indicationPersonSelectManager.showConfirmWithReason}
                validationMessage={indicationPersonSelectManager.validationMessage}
                onEdit={indicationPersonSelectManager.handleEditSelect}
                onCancel={indicationPersonSelectManager.handleCancelEdit}
                onSave={indicationPersonSelectManager.handleSaveSelect}
                onConfirmValidation={indicationPersonSelectManager.handleConfirmValidation}
                onCancelValidation={indicationPersonSelectManager.handleCancelValidation}
                onConfirmWithReason={indicationPersonSelectManager.handleConfirmWithReason}
                onCancelWithReason={indicationPersonSelectManager.handleCancelWithReason}
                onValueChange={indicationPersonSelectManager.setEditedValue}
                allowEdit={allowEdit}
              />
            </Col>
            <Col xs={8} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Trạng thái:" className="spaces min-w-110" />
              <span className="fw-light text-truncate">
                {donVatTuInfo?.statusName || '...'}
              </span>
            </Col>
            <Col xs={4} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Kho:" className="spaces min-w-110" />
              <span className="fw-light text-truncate">
                {donVatTuInfo?.warehouseName || '...'}
              </span>
            </Col>
            <Col xs={8} className="d-flex align-items-center text-lable-input mb-3">
              <LabelRequired label="Chẩn đoán:" className="spaces min-w-110" />
              <span className="fw-light text-truncate">
                {donVatTuInfo?.provisionalDiagnosisName || '...'}
              </span>
            </Col>
          </Row>
        </div>
        <div className="px-10 py-5 spaces mb-4">
          <InputSearch
            value={""}
            placeholder="Tìm kiếm"
            handleChange={() => { }}
            // handleKeyDown={onKeydown}
            // handleSearch={onBtnSearchClick}
            className="spaces h-25 radius-3"
          />
        </div>
        <div className="px-7 spaces pb-4">
          <DanhSachPhieu
            dsPhieu={dsPhieu || []}
            handleSelectRowData={(data) => setSelectedPhieu(data)}
            title="price"
            isGetFirstData
          />
        </div>
        <TableCustom<any>
          className="overflow-auto spaces h-calc-vh-425"
          data={donVatTuInfo?.lstMedicalItemOrderRes?.map((row: any) => ({
            ...row,
            maVatTu: row.medicalItemCode,
            tenVatTu: row.name,
            donVi: row.unit,
            soLuong: row.totalQuantity,
            donGia: row.unitPrice,
            thanhTien: row.intoMoney,
          })) || []}
          columns={vatTuColumn}
        />

        <ThaoTacPhieuVatTu donVatTuInfo={donVatTuInfo} getDanhSachDonVatTu={handleGetDsPhieu} handleSelectPhieu={handleSelectPhieu} allowOrder={allowOrder} />
      </div>
    </Row>
  )
}

export default VatTu;

type ActionType = 'edit' | 'delete' | 'cancel';

type DialogActionProps = {
  open: boolean;
  action: ActionType;
};

const ThaoTacPhieuVatTu = ({ getDanhSachDonVatTu, donVatTuInfo, handleSelectPhieu, allowOrder = true }: {
  getDanhSachDonVatTu: () => void;
  donVatTuInfo: any;
  handleSelectPhieu: (id: number | string) => void;
  allowOrder?: boolean
}) => {
  const { benhNhanHanhChinh, statusChangedEventFunc } = usePhanHeNoiTruContext();

  const isXuatKho = [
    WORKFLOW_MEDICAL_ITEM_STATUS_CODE.CHO_XUAT_KHO,
    WORKFLOW_MEDICAL_ITEM_STATUS_CODE.DA_XUAT_KHO,
  ].includes(donVatTuInfo?.statusCode);

  const [guiPhieu, setGuiPhieu] = useState(false);
  const [dialogAction, setDialogAction] = useState<DialogActionProps>({
    open: false,
    action: 'edit',
  });
  const [keyOpenPhieuIn, setKeyOpenPhieuIn] = useState<string>('');
  const [isUpdateWorkflow, setIsUpdateWorkflow] = useState(false);

  const onDeleteDonVatTu = async () => {
    try {
      const response = await deleteDonThuoc(donVatTuInfo.id);
      if (CODE.SUCCESS === response.code) {
        toast.success(RESPONSE_MESSAGE.DELETE.SUCCESS);
        setDialogAction({
          open: false,
          action: 'edit',
        });
        getDanhSachDonVatTu();
        statusChangedEventFunc?.();
      }
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
    }
  };

  const handlePressKeyShortcuts = (e: any) => {
    switch (e.key) {
      case KEY.F3:
        e.preventDefault();
        setKeyOpenPhieuIn('F3');
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handlePressKeyShortcuts);
    return () => {
      window.removeEventListener('keydown', handlePressKeyShortcuts);
    };
  }, []);

  useEffect(() => {
    setGuiPhieu(donVatTuInfo?.statusCode === WORKFLOW_MEDICAL_ITEM_STATUS_CODE.GUI_DON);
  }, [donVatTuInfo?.statusCode]);

  const WorkFlowButton = () => {
    return (
      <WfButton
        tracker={WfConstTrackerClinical.MEDICAL_ITEMS}
        objectId={donVatTuInfo?.id}
        prefixFunction={(statusCode: string, wfCallbackFn: () => void, cancel?: () => void) => {
          if (WORKFLOW_MEDICAL_ITEM_STATUS_CODE.CHUA_GUI_DON === statusCode) {
            onGuiDonVatTu(wfCallbackFn, cancel);
            return;
          }

          onHuyGuiDonVatTu(wfCallbackFn, cancel);
        }}
        additionalFunc={(status: any) => {
          onUpdateWorkflowStatus(status);
        }}
        forceRender={isUpdateWorkflow}
      ></WfButton>
    );
  };

  const onGuiDonVatTu = async (callbackFn: () => void, cancel?: () => void) => {
    try {
      const { data } = await updateKeThuoc({
        ...donVatTuInfo,
        encounterId: donVatTuInfo?.encounterId,
        orderTypeId: parseInt(ORDER_TYPE_ID.VAT_TU),
        lstMedicalItemOrder: donVatTuInfo?.lstMedicalItemOrderRes,
        formCode: REQUEST_FROM.DIEU_TRI,
        medicalItemTypeCode: REQUEST_TYPE.VAT_TU,
        visitObjectTypeCode: benhNhanHanhChinh?.visitObjectTypeCode
      });
      if (CODE.SUCCESS === data?.code) {
        toast.success('Gửi đơn vật tư thành công');
        callbackFn();
      } else {
        cancel?.();
      }
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
      cancel?.();
    }
  };

  const onHuyGuiDonVatTu = async (callbackFn: () => void, cancel?: () => void) => {
    try {
      const { code } = await huyGuiDonThuoc(donVatTuInfo?.id);
      if (CODE.SUCCESS === code) {
        toast.success('Huỷ gửi đơn vật tư thành công');
        callbackFn();
      } else {
        cancel?.();
      }
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
      cancel?.();
    }
  };

  const onUpdateWorkflowStatus = (workflowStatus: string) => {
    setGuiPhieu(workflowStatus === WORKFLOW_MEDICAL_ITEM_STATUS_CODE.GUI_DON);
    handleSelectPhieu(donVatTuInfo?.id);
  };

  const PrintButton = () => {
    return (
      <ButtonIn<IInPhieuChiDinhThuocParams>
        fetchExport={printPhieuChiDinhThuoc}
        params={{
          fileType: 'pdf',
          patientId: benhNhanHanhChinh?.patientId,
          medicalItemGroupId: donVatTuInfo?.id,
          visitId: benhNhanHanhChinh?.visitId,
          type: parseInt(ORDER_TYPE_ID.VAT_TU),
        }}
        label="In đơn vật tư (F3)"
        keyOpenPhieuIn={keyOpenPhieuIn}
        setKeyOpenPhieuIn={setKeyOpenPhieuIn}
        keyBtn={KEY.F3}
      />
    );
  };

  if (!donVatTuInfo?.id) return <div className="bg-white spaces min-h-50"></div>;

  if (isXuatKho) {
    return <div className="d-flex gap-3 bg-white p-2">{PrintButton()}</div>;
  }

  return (
		<div className="d-flex gap-2 justify-content-start p-2 bg-white">
			{guiPhieu ? (
				<>
					{PrintButton()}
          {donVatTuInfo?.isAllowModify && allowOrder && WorkFlowButton()}
				</>
			) : (
				<>
					{donVatTuInfo?.isAllowModify &&
						![WORKFLOW_MEDICAL_ITEM_STATUS_CODE.DANG_TONG_HOP].includes(
							donVatTuInfo?.statusCode
              ) && allowOrder && (
							<>
								<Button
									onClick={() => {
										setDialogAction({
											open: true,
											action: 'edit',
										});
									}}
								>
									Sửa phiếu
								</Button>
								{WorkFlowButton()}
								<Button
									className="btn-danger"
									onClick={() => {
										setDialogAction({
											open: true,
											action: 'delete',
										});
									}}
								>
									Xóa phiếu
								</Button>
							</>
						)}
				</>
			)}

			{dialogAction.open && dialogAction.action === 'edit' && (
				<ChiDinhVatTu
					open={dialogAction.open}
					onClose={() => {
						setDialogAction({
							open: false,
							action: 'edit',
						});
					}}
					onSave={() => {
						setIsUpdateWorkflow(!isUpdateWorkflow);
						handleSelectPhieu(donVatTuInfo?.id);
					}}
					itemEdit={donVatTuInfo}
				/>
			)}

			{dialogAction.open && ['delete'].includes(dialogAction.action) && (
				<ConfirmDialog
					className="z-index-1060"
					show={dialogAction.open}
					title="Thông báo"
					message={`Bạn có chắc chắn muốn xóa đơn vật tư này không ?`}
					yes="Xác nhận"
					close="Đóng"
					onCloseClick={() => {
						setDialogAction({
							open: false,
							action: 'edit',
						});
					}}
					onYesClick={onDeleteDonVatTu}
				/>
			)}
		</div>
  );
};